import { useTemplateUsage } from "@features/bx/billTemplateV2/hooks";
import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useParams } from "react-router-dom";

export const TemplateUsageTab = () => {
  const CURRENCY = useCurrency();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = MODAL_PAGE_SIZE;
  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const { data: templateUsage } = useTemplateUsage(id);

  // Define columns for the table
  const columns: TableColumn<any>[] = [
    {
      key: "billNumber",
      header: "Invoice No",
      type: "text",
    },
    {
      key: "createdDate",
      header: "Invoice Date",
      type: "text",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
    },
    {
      key: "remoteConsumerNumber",
      header: "Account No",
      type: "text",
    },
    {
      key: "billAmount",
      header: "Amount",
      type: "custom",
      sortable: true,
      render: (value: number) => `${CURRENCY} ${value}`,
    },
  ];

  // Define actions for each row
  const actions: TableAction<any>[] = [
    {
      label: "View Bill",
      onClick: (row) => {
        handleViewClick(row.id);
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Download Bill",
      onClick: (row) => {
        handleDownloadClick(row.id);
        // Trigger download
      },
      icon: "Download",
      variant: "outline",
    },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    console.log("Page changed to:", newPage);
  };

  // Calculate pagination
  const totalItems = templateUsage?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Bills Generated</h3>
        <p className="text-sm text-gray-600 mt-1">
          List of all bills generated using this template
        </p>
      </div>

      <GenericTable
        data={templateUsage?.result}
        columns={columns}
        rowKey="id"
        loading={false}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills generated using this template yet"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
